import { all, call, fork, put, takeEvery } from "redux-saga/effects";
import {
  INICIA_CURSO,
  SALVA_CURSO,
  ATUALIZA_CURSO,
  BUSCA_CURSO_EDICAO,
  REMOVE_CURSO,
  LISTA_CURSO,
  ON_HIDE_LOADER,
  LISTA_ANOS_LETIVOS_POR_CURSO,
  LISTA_ESCOLAS_CURSO,
  LISTA_ANOS_LETIVOS_POR_ESCOLA_CURSO
} from "constants/ActionTypes";
import requisicaoApi from 'util/Api';
import { showAuthMessage } from "actions/Curso";
import {
  salvaCursoSucesso,
  removeCursoSucesso,
  inicioCursoSucesso,
  buscaCursoEdicaoSucesso,
  listaCursoSucesso,
  listaAnosLetivosPorCursoSucesso,
  listaEscolasCursoSucesso,
  listaAnosLetivosPorEscolaCursoSucesso
} from "actions/Curso";

/*#################################################################################################################*/
/* ---------------------------------- Helpers de Formatação --------------------------------------------------------*/

// O backend está retornando mensagens com propriedades em PascalCase (ex.: NivelEnsinoId, TipoEnsinoId, QuantidadeEtapas, HoraFaltaMin, CargaHoraria).
// Para compatibilizar, normalizamos o payload enviado para essas chaves específicas em PascalCase e
// convertemos os valores numéricos para números (não strings).
const toNumberOrNull = (v) => {
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'number') return Number.isNaN(v) ? null : v;
  if (typeof v === 'string') {
    const n = Number(v);
    return Number.isNaN(n) ? null : n;
  }
  // Se for objeto, tenta extrair id/value
  if (typeof v === 'object') {
    const cand = v.id ?? v.value ?? (typeof v.valor !== 'undefined' ? v.valor : undefined);
    if (cand === undefined || cand === null) return null;
    return toNumberOrNull(cand);
  }
  return null;
};

const extractIdNumber = (v) => {
  // Aceita formatos: número, string numérica, {id}, {value}, {value: {id}}
  if (v === undefined || v === null || v === '') return null;
  if (typeof v === 'object') {
    if (typeof v.value === 'object') return toNumberOrNull(v.value.id ?? v.value.value ?? v.value);
    return toNumberOrNull(v.id ?? v.value ?? v);
  }
  return toNumberOrNull(v);
};

const normalizeCursoPayload = (p) => {
  if (!p || typeof p !== 'object') return p;
  const out = {};
  // Id pode vir como 0/"0"; mantém número
  out.Id = toNumberOrNull(p.Id ?? p.id);
  // Strings
  out.Nome = p.Nome ?? p.nome ?? '';
  out.Modalidade = p.Modalidade ?? p.modalidade ?? '';
  out.Descricao = p.Descricao ?? p.descricao ?? '';
  // Numéricos
  out.CargaHoraria = toNumberOrNull(p.CargaHoraria ?? p.cargaHoraria ?? p.carga_horaria);
  out.QuantidadeEtapas = toNumberOrNull(p.QuantidadeEtapas ?? p.qtdEtapas ?? p.qtd_etapas);
  out.HoraFaltaMin = toNumberOrNull(p.HoraFaltaMin ?? p.horaFalta ?? p.hora_falta);
  out.NivelEnsinoId = extractIdNumber(p.NivelEnsinoId ?? p.nivelEnsinoId ?? p.nivel_ensino_id);
  out.TipoEnsinoId = extractIdNumber(p.TipoEnsinoId ?? p.tipoEnsinoId ?? p.tipo_ensino_id);
  out.TipoRegimeId = extractIdNumber(p.TipoRegimeId ?? p.tipoRegimeId ?? p.tipo_regime_id);
  return out;
};

/*#################################################################################################################*/
/* --------------------------------------------- Requests do tipo GET ---------------------------------------------*/

const getCursos = async () =>
  await requisicaoApi.get('/cursos')
    .then(resposta => resposta)
    .catch(error => error);

const getCursoEdicao = async (id) =>
  await requisicaoApi.get('/cursos/' + id)
    .then(resposta => resposta)
    .catch(error => error);

const getListaCurso = async (filtros) => {
  const params = {};
  if (filtros) {
    if (typeof filtros === 'string') {
      // Proteção contra bug onde o objeto de filtro vinha serializado como string
      try {
        const parsed = JSON.parse(filtros);
        if (parsed && typeof parsed === 'object' && (parsed.Nome !== undefined || parsed.EscolaId !== undefined)) {
          if (parsed.Nome) params.Nome = parsed.Nome;
          if (parsed.EscolaId) params.EscolaId = parsed.EscolaId;
          if (parsed.AnoLetivo) params.AnoLetivo = parsed.AnoLetivo;
        } else {
          params.Nome = filtros;
        }
      } catch (e) {
        params.Nome = filtros;
      }
    } else {
      const nome = filtros.Nome || filtros.nome;
      if (nome && typeof nome === 'string') params.Nome = nome;

      const escolaId = filtros.EscolaId || filtros.escolaId;
      if (escolaId) params.EscolaId = escolaId;

      const anoLetivo = filtros.AnoLetivo || filtros.anoLetivo;
      if (anoLetivo) params.AnoLetivo = anoLetivo;
    }
  }

  return await requisicaoApi.get('/cursos', { params })
    .then(resposta => resposta)
    .catch(error => error);
};

const getAnosLetivosPorCurso = async (id) =>
  await requisicaoApi.get(`/cursos/${id}/anos-letivos-iniciados`)
    .then(resposta => resposta)
    .catch(error => error);

const getEscolas = async () =>
  await requisicaoApi.get('/escolas')
    .then(resposta => resposta)
    .catch(error => error);

const getAnosLetivosPorEscola = async (idEscola) =>
  await requisicaoApi.get(`/escolas/${idEscola}/anos-letivos`)
    .then(resposta => resposta)
    .catch(error => error);

/*#################################################################################################################*/
/* ------------------------------------- Requests do tipo POST, PUT E DELETE --------------------------------------*/

// Suporta tanto JSON quanto FormData. Quando receber FormData, axios define o content-type automaticamente.
const adicionaCursoRequest = async (curso) =>
  await requisicaoApi.post('/cursos', curso)
    .then(resposta => resposta)
    .catch(error => error);

const atualizaCursoRequest = async (curso) =>
  await requisicaoApi.put('/cursos/' + (curso.id || curso.Id || ''), curso)
    .then(resposta => resposta)
    .catch(error => error);

const deleteCursoRequest = async (id) =>
  await requisicaoApi.delete('/cursos/' + id)
    .then(resposta => resposta)
    .catch(error => error);

/*#################################################################################################################*/
/* ------------------------------------------ Funções das Sagas ---------------------------------------------------*/

// Normaliza diferentes formatos de resposta do backend em uma lista de cursos
const normalizeListaCursos = (data) => {
  if (Array.isArray(data)) return data;
  if (data && Array.isArray(data.curso)) return data.curso;
  if (data && Array.isArray(data.Curso)) return data.Curso;
  if (data && typeof data === 'object') return [data];
  return [];
};

function* fnIniciaCurso() {
  try {
    const retorno = yield call(getCursos);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      const listaNormalizada = normalizeListaCursos(retorno.data);
      yield put(inicioCursoSucesso(listaNormalizada));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    // Garante desligar o loader mesmo se algum fluxo acima não o fizer
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnBuscaCursoEdicao({ payload }) {
  try {
    const retorno = yield call(getCursoEdicao, payload);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(buscaCursoEdicaoSucesso(retorno.data));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnListaCurso({ payload }) {
  try {
    const retorno = yield call(getListaCurso, payload);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      const listaNormalizada = normalizeListaCursos(retorno.data);
      yield put(listaCursoSucesso(listaNormalizada));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    // Evita manter loader ativo caso sucesso/erro não disparem ações esperadas
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnListaAnosLetivosPorCurso({ payload }) {
  try {
    const retorno = yield call(getAnosLetivosPorCurso, payload);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      const data = retorno.data;
      const rawLista = Array.isArray(data) ? data : (Array.isArray(data?.anosLetivos) ? data.anosLetivos : (Array.isArray(data?.data) ? data.data : []));
      const listaNormalizada = rawLista.map(ano => {
        if (typeof ano === 'object' && ano !== null) {
          const id = ano.id || ano.ano || ano.Id || ano.Ano || 0;
          const title = String(ano.title || ano.ano || ano.nome || ano.Nome || id);
          return { id: Number(id), title };
        }
        return { id: Number(ano || 0), title: String(ano || '') };
      });
      yield put(listaAnosLetivosPorCursoSucesso(listaNormalizada));
      const len = Array.isArray(listaNormalizada) ? listaNormalizada.length : 0;
      if (len === 0) {
        yield put(showAuthMessage('O curso selecionado não possui nenhum ano letivo iniciado.'));
      }
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnSalvaCurso({ payload }) {
  try {
    // Normaliza para PascalCase + tipos numéricos
    const payloadNormalized = normalizeCursoPayload(payload);
    const retorno = yield call(adicionaCursoRequest, payloadNormalized);
    if (retorno.message) {
      const status = retorno?.response?.status;
      const data = retorno?.response?.data;
      if (status === 400) {
        let mensagemValidacao = '';
        let inconsistencias = data?.inconsistencias || [];
        if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
          // Ex.: [{ propriedade: 'Campo', mensagem: 'Descrição do erro' }]
          mensagemValidacao = inconsistencias[0].mensagem;
        } else if (data && typeof data === 'object') {
          mensagemValidacao = data.mensagem || JSON.stringify(data);
        }
        yield put(showAuthMessage(mensagemValidacao || retorno.message));
      } else {
        yield put(showAuthMessage(JSON.stringify(retorno.message)));
      }
    } else {
      yield put(salvaCursoSucesso('Curso salvo com sucesso!'));
      // Após salvar, força atualização da listagem usando o fluxo padrão
      yield put({ type: LISTA_CURSO, payload: { nome: '' } });
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnAtualizaCurso({ payload }) {
  try {
    // Normaliza para PascalCase + tipos numéricos
    const payloadNormalized = normalizeCursoPayload(payload);
    const retorno = yield call(atualizaCursoRequest, payloadNormalized);
    if (retorno.message) {
      const status = retorno?.response?.status;
      const data = retorno?.response?.data;
      if (status === 400) {
        let mensagemValidacao = '';
        let inconsistencias = data?.inconsistencias || [];
        if (Array.isArray(inconsistencias) && inconsistencias.length > 0) {
          mensagemValidacao = inconsistencias[0].mensagem;
        } else if (data && typeof data === 'object') {
          mensagemValidacao = data.mensagem || JSON.stringify(data);
        }
        yield put(showAuthMessage(mensagemValidacao || retorno.message));
      } else {
        yield put(showAuthMessage(JSON.stringify(retorno.message)));
      }
    } else {
      yield put(salvaCursoSucesso('Curso atualizado com sucesso!'));
      // Após atualizar, força atualização da listagem usando o fluxo padrão
      yield put({ type: LISTA_CURSO, payload: { nome: '' } });
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnRemoveCurso({ payload }) {
  try {
    const retorno = yield call(deleteCursoRequest, payload);

    // Verifica se houve erro de validação (status 400 ou sucesso: false)
    const data = retorno?.response?.data || retorno?.data;

    // Tratamento para quando a API retorna erro 400 com inconsistências
    if (retorno?.response?.status === 400 || (data && data.sucesso === false)) {
      let mensagemErro = '';
      if (Array.isArray(data?.inconsistencias) && data.inconsistencias.length > 0) {
        mensagemErro = data.inconsistencias[0].mensagem;
      } else if (Array.isArray(data?.erros) && data.erros.length > 0) {
        mensagemErro = data.erros[0].mensagem;
      } else {
        mensagemErro = data?.mensagem || 'Erro ao excluir curso.';
      }
      yield put(showAuthMessage(mensagemErro));
      return;
    }

    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(removeCursoSucesso('Curso removido com sucesso!'));
      yield put({ type: LISTA_CURSO, payload: { nome: '' } });
    }
  } catch (error) {
    // Tenta extrair mensagem amigável de erro se disponível
    const msg = error?.response?.data?.mensagem ||
      (error?.response?.data?.inconsistencias?.[0]?.mensagem) ||
      JSON.stringify(error);
    yield put(showAuthMessage(msg));
  } finally {
    yield put({ type: ON_HIDE_LOADER });
  }
}

function* fnListaEscolasCurso() {
  try {
    const retorno = yield call(getEscolas);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      yield put(listaEscolasCursoSucesso(retorno.data));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

function* fnListaAnosLetivosPorEscolaCurso({ payload }) {
  try {
    const escolaId = payload?.escolaId || (payload && typeof payload !== 'object' ? payload : 0);
    if (!escolaId || Number(escolaId) === 0) {
      yield put(listaAnosLetivosPorEscolaCursoSucesso([]));
      return;
    }
    const retorno = yield call(getAnosLetivosPorEscola, escolaId);
    if (retorno.message) {
      yield put(showAuthMessage(JSON.stringify(retorno.message)));
    } else {
      const dataAnos = retorno.data;
      const listaAnos = Array.isArray(dataAnos)
        ? dataAnos
        : (Array.isArray(dataAnos?.anosLetivosAtivos) ? dataAnos.anosLetivosAtivos
          : (Array.isArray(dataAnos?.anosLetivos) ? dataAnos.anosLetivos
            : (Array.isArray(dataAnos?.ativos) ? dataAnos.ativos : [])));

      const listaNormalizada = listaAnos.map(item => {
        if (typeof item === 'object') return { id: item.ano || item.Ano || item.id, title: String(item.ano || item.Ano || item.descricao || item.id) };
        return { id: item, title: String(item) };
      });

      yield put(listaAnosLetivosPorEscolaCursoSucesso(listaNormalizada));
    }
  } catch (error) {
    yield put(showAuthMessage(JSON.stringify(error)));
  }
}

export function* iniciaAcoesCurso() {
  yield takeEvery(INICIA_CURSO, fnIniciaCurso);
}

export function* salvaCurso() {
  yield takeEvery(SALVA_CURSO, fnSalvaCurso);
}

export function* atualizaCurso() {
  yield takeEvery(ATUALIZA_CURSO, fnAtualizaCurso);
}

export function* buscaCursoEdicao() {
  yield takeEvery(BUSCA_CURSO_EDICAO, fnBuscaCursoEdicao);
}

export function* removeCurso() {
  yield takeEvery(REMOVE_CURSO, fnRemoveCurso);
}

export function* listaCurso() {
  yield takeEvery(LISTA_CURSO, fnListaCurso);
}

export function* listaAnosLetivosPorCurso() {
  yield takeEvery(LISTA_ANOS_LETIVOS_POR_CURSO, fnListaAnosLetivosPorCurso);
}

export default function* rootSaga() {
  yield all([
    fork(iniciaAcoesCurso),
    fork(salvaCurso),
    fork(atualizaCurso),
    fork(buscaCursoEdicao),
    fork(removeCurso),
    fork(listaCurso),
    fork(listaAnosLetivosPorCurso),
    takeEvery(LISTA_ESCOLAS_CURSO, fnListaEscolasCurso),
    takeEvery(LISTA_ANOS_LETIVOS_POR_ESCOLA_CURSO, fnListaAnosLetivosPorEscolaCurso)
  ]);
}
